import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/estimage-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | esti-mate Alternative for UK Builders",
  "description": "Looking for an esti-mate alternative for UK builders? The Proper Tool offers AI-powered quoting, a 31-category UK cost library, and a 14-day free trial from £29/mo.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "esti-mate Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best esti-mate alternative for UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "esti-mate is current construction estimating software at estimate.co.uk. Its UK version starts at £1,515 excluding VAT, subject to an annual licence fee, and includes a 20-day free trial. For Smart Quoting, The Proper Tool starts at £29/month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "Is esti-mate good for residential builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "esti-mate is a traditional UK construction estimating and takeoff product. It runs on Windows platforms and most Windows networks, with optional UK BCIS Wessex pricing libraries.",
      },
    },
    {
      "@type": "Question",
      "name": "How does esti-mate pricing compare to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The esti-mate UK version starts at £1,515 excluding VAT and is subject to an annual licence fee. It offers a 20-day free trial. Optional BCIS Wessex pricing libraries start at £410.",
      },
    },
    {
      "@type": "Question",
      "name": "Can esti-mate read drawings or generate quotes automatically?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "esti-mate is traditional estimating and takeoff software. Its optional On-Screen Take Off costs £455, measures PDF drawings and links quantities into an estimate, but this is manual/assisted takeoff rather than autonomous drawing interpretation.",
      },
    },
    {
      "@type": "Question",
      "name": "Does esti-mate have a mobile app?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "esti-mate runs on Windows platforms and most Windows networks. Windows-device access is limited rather than native mobile support; no native iOS or Android app is advertised, and cloud access should not be assumed.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  estimage: string;
  estimageState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "UK construction pricing built in", estimage: "Optional BCIS Wessex libraries from £410", estimageState: "yes", tpt: "Yes: 31 UK trade categories", tptState: "yes" },
  { feature: "Smart Quoting", estimage: "No: traditional estimating", estimageState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and PDFs", estimage: "Partial: optional £455 On-Screen Take Off", estimageState: "partial", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "Cloud-native (no install required)", estimage: "No: Windows software", estimageState: "no", tpt: "Yes: browser-based", tptState: "yes" },
  { feature: "Mobile-friendly", estimage: "Partial: Windows-device access; no native iOS/Android advertised", estimageState: "partial", tpt: "Yes", tptState: "yes" },
  { feature: "Scope completeness validation", estimage: "No", estimageState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "Labour / materials / plant split", estimage: "Yes: detailed estimating", estimageState: "yes", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Branded PDF quote output", estimage: "Yes", estimageState: "yes", tpt: "Yes", tptState: "yes" },
  { feature: "Free trial", estimage: "20 days", estimageState: "yes", tpt: "14-day free trial", tptState: "yes" },
  { feature: "Starting price", estimage: "£1,515 + VAT, annual licence fee", estimageState: "partial", tpt: "£29/month (per account)", tptState: "yes" },
  { feature: "Time to first quote", estimage: "Setup and learning required", estimageState: "partial", tpt: "Under 10 minutes", tptState: "yes" },
  { feature: "Credit card required to trial", estimage: "Yes", estimageState: "no", tpt: "No", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
      title: "Traditional estimating workflow",
      desc: "esti-mate is designed for manual construction estimating and takeoff. It does not provide Smart Quoting or evidence of scope-to-priced-quote generation from a description.",
  },
  {
      title: "Windows-based rather than cloud-native",
      desc: "The official site says esti-mate runs on Windows platforms and most Windows networks. Windows-device access is limited; no native iOS or Android support is advertised.",
  },
  {
      title: "Optional libraries and takeoff add-ons",
      desc: "The UK version starts at £1,515 excluding VAT, subject to an annual licence fee. Optional BCIS Wessex pricing libraries start at £410 and On-Screen Take Off costs £455.",
  },
  {
      title: "Best suited to traditional takeoff",
      desc: "esti-mate can suit teams that want established Windows estimating workflows and optional UK cost libraries, but it is not positioned as an autonomous drawing interpretation or Smart Quoting tool.",
  },
];

const STAY_ON_ESTIMAGE = [
  "You are deeply embedded in esti-mate's workflow and have invested significant time building templates and pricing libraries.",
  "You value the software's long UK heritage and want a tool that has been proven in the UK market for over 20 years.",
  "Your work involves large, detailed traditional material takeoffs where manual item selection is the established process.",
  "You do not need mobile access or AI-assisted scope generation.",
];

const SWITCH_REASONS = [
  "You want to generate quotes from drawings or a description without manually selecting every item.",
  "You need cloud access and mobile compatibility so you can quote from anywhere.",
  "You want all your features in a single, predictable monthly subscription rather than separate modules.",
  "You need a longer free trial period to properly test the software on real jobs before committing.",
  "You want a tool with active development and modern AI capabilities built around UK construction.",
  "You want to be quoting on day one without a learning curve.",
];

const FAQS = [
  {
    q: "What is the best esti-mate alternative for UK builders?",
    a: "For UK builders who want modern, AI-powered quoting rather than the desktop-era modular workflow of esti-mate, The Proper Tool is the strongest alternative. It generates fully itemised scopes from drawings or a plain description using AI, has a 31-category UK cost library built in, and starts at £29/month with a 14-day free trial.",
  },
  {
    q: "Is esti-mate good for residential builders?",
    a: "esti-mate has been around since 2001 and has a loyal following among UK builders. However, it requires all items to be entered manually, has no AI scope generation, and does not read drawings or photos. For builders who want to quote faster with less manual input, it is a significant limitation.",
  },
  {
    q: "How does esti-mate pricing compare to The Proper Tool?",
    a: "esti-mate uses a modular pricing structure where separate modules are purchased for estimating, pricing, and reporting. The Proper Tool starts at £29 per month with all features included and a 14-day free trial, with no credit card required.",
  },
  {
    q: "Can esti-mate read drawings or generate quotes automatically?",
    a: "No. esti-mate does not read architectural drawings or PDFs, and all items must be selected and entered manually. The Proper Tool reads uploaded structural drawing PDFs using AI, extracting beam sizes, floor areas, and dimensions to generate a more complete quote.",
  },
  {
    q: "Does esti-mate have a mobile app?",
    a: "esti-mate was built in the desktop-software era and does not have a native mobile app. The Proper Tool is cloud-native and works on any device, including mobile, without any installation required.",
  },
];

export default function EstimageAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="esti-mate Alternative for UK Builders: AI-Powered Quoting Without the Manual Entry | The Proper Tool"
        description="Looking for an esti-mate (estimate.co.uk) alternative? The Proper Tool offers AI quote generation from drawings, a 31-category UK cost library, and a 14-day free trial from £29/mo."
        canonical="/estimage-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>esti-mate Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              esti-mate Alternative for UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              esti-mate is current UK construction estimating software at <a href="https://estimate.co.uk" className="text-primary hover:underline">estimate.co.uk</a>. It provides traditional Windows estimating and takeoff, with optional UK pricing libraries and a 20-day free trial.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why builders look for an esti-mate alternative</h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              esti-mate is a current construction product. Its official information describes a traditional Windows workflow with optional pricing libraries and takeoff:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs esti-mate: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">esti-mate</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.estimage} state={row.estimageState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
               Competitor information was checked on 19 September 2026 against estimate.co.uk. Optional BCIS Wessex libraries and On-Screen Take Off are separate additions.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              The UK version starts at £1,515 excluding VAT and is subject to an annual licence fee. esti-mate offers a 20-day free trial; optional BCIS Wessex pricing libraries start at £410 and On-Screen Take Off costs £455. The Proper Tool offers a 14-day free trial with no credit card required.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">esti-mate</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                   <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />From £1,515 excluding VAT, subject to an annual licence fee</li>
                   <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />20-day free trial</li>
                   <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Optional BCIS Wessex libraries from £410; On-Screen Take Off £455</li>
                   <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Windows-based traditional estimating; no Smart Quoting</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month, all features included</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />Cloud-native, works on any device, first quote in under 10 minutes</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on esti-mate</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              esti-mate is a current UK construction estimating tool. If the following describes your situation, it may be the right choice:
            </p>
            <div className="space-y-3">
              {STAY_ON_ESTIMAGE.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons builders switch from esti-mate to The Proper Tool</h2>
              <p className="text-lg text-muted-foreground mb-8">The switch is typically driven by speed, browser access, and the desire for Smart Quoting.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
               <strong className="text-foreground">Disclaimer:</strong> Competitor information was checked on 19 September 2026 against estimate.co.uk. Optional features and pricing should be confirmed directly with esti-mate.
            </p>
          </div>
        </section>

        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all quoting tools for UK builders" },
                { href: "/estimatorxpress-alternative", label: "EstimatorXpress Alternative" },
                { href: "/buildxact-alternative", label: "Buildxact Alternative UK" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg border border-border p-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  {link.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
